import { useEffect, useState } from 'react';

/**
 * Keep the screen on while `active` (Screen Wake Lock API). The browser drops the lock when the tab is hidden, so it
 * is asked for again when the page becomes visible. Where it is unsupported or refused (battery saver, policy) the
 * screen follows its own sleep settings: `held` is then false, so the page can say the screen may go dark.
 */
export function useWakeLock(active: boolean) {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) {
      setHeld(false);
      return;
    }
    let lock: WakeLockSentinel | null = null;
    let stopped = false;
    const request = async () => {
      if (stopped || document.visibilityState !== 'visible' || (lock && !lock.released)) return;
      try {
        const got = await navigator.wakeLock.request('screen');
        if (stopped) return void got.release().catch(() => undefined);
        lock = got;
        setHeld(true);
        // the browser lets go when the tab is hidden: asked again when it is visible
        got.addEventListener('release', () => !stopped && setHeld(false));
      } catch {
        if (!stopped) setHeld(false);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
  return held;
}
